import { useState } from 'react';
import { Link } from 'react-router-dom';

import { registerProfessionalLead } from '@/features/leads/services/leads.service';
import { extractErrorMessage } from '@/lib/api';

interface FaqCategory {
  title: string;
  items: { question: string; answer: string }[];
}

const categories: FaqCategory[] = [
  {
    title: 'Candidatura e Selezione',
    items: [
      {
        question: 'Come posso candidarmi come consulente su Klaro7?',
        answer:
          'Per entrare a far parte di Klaro7 è necessario compilare il modulo di candidatura disponibile sulla piattaforma. Dopo la registrazione, riceverai un link per completare un questionario conoscitivo.',
      },
      {
        question: 'Come funziona il processo di selezione?',
        answer:
          'Il processo si articola in più fasi: compilazione del questionario, valutazione del profilo ed eventuale prova pratica. Solo i candidati ritenuti idonei verranno selezionati.',
      },
      {
        question: "È garantito l'accesso alla piattaforma?",
        answer:
          'No. Klaro7 seleziona un numero limitato di consulenti per garantire qualità e professionalità.',
      },
    ],
  },
  {
    title: 'Attività e Funzionamento',
    items: [
      {
        question: 'Come funziona il lavoro sulla piattaforma?',
        answer:
          'Una volta attivo, potrai ricevere consulti da utenti tramite telefono, chat e videochiamata. Gestirai la tua disponibilità e i tuoi orari direttamente dalla dashboard.',
      },
      {
        question: 'Devo rispettare degli orari?',
        answer:
          'Non ci sono orari fissi, ma è richiesta una presenza costante e affidabile. Per garantire un servizio attivo 24/7, i consulenti possono essere coinvolti anche nelle fasce serali e notturne.',
      },
    ],
  },
  {
    title: 'Messaggistica Utenti',
    items: [
      {
        question: 'Posso contattare gli utenti per primo?',
        answer: 'No. Il consulente non può mai iniziare una conversazione.',
      },
      {
        question: 'Quando posso ricevere messaggi dagli utenti?',
        answer:
          "Puoi ricevere messaggi solo quando sei offline e l'utente ti ha inserito tra i preferiti.",
      },
      {
        question: 'A cosa servono i messaggi?',
        answer:
          'I messaggi servono esclusivamente per concordare la disponibilità per un consulto. Non sostituiscono il consulto stesso. Limiti: massimo 7 messaggi al giorno per utente e la conversazione ha durata limitata.',
      },
    ],
  },
  {
    title: 'Preferiti e Visibilità',
    items: [
      {
        question: 'Cosa significa essere nei preferiti di un utente?',
        answer: "Significa che l'utente ti ha selezionato tra i consulenti di fiducia.",
      },
      {
        question: 'Essere nei preferiti porta vantaggi?',
        answer:
          'Sì: maggiore probabilità di essere contattato, maggiore fidelizzazione e più consulti nel tempo.',
      },
    ],
  },
  {
    title: 'Guadagni e Tariffe',
    items: [
      {
        question: 'Posso scegliere la mia tariffa?',
        answer:
          "Sì, entro i limiti stabiliti dalla piattaforma. Le tariffe possono variare in base all'esperienza e al numero di consulti effettuati.",
      },
      {
        question: 'Come funzionano i guadagni?',
        answer:
          'Il guadagno dipende da: numero di consulti, durata delle chiamate e tariffa applicata.',
      },
      {
        question: 'Ci sono livelli di crescita?',
        answer:
          "Sì. I consulenti possono accedere a fasce tariffarie più alte in base alle performance e all'esperienza maturata sulla piattaforma.",
      },
    ],
  },
  {
    title: 'Recensioni e Qualità',
    items: [
      {
        question: 'Le recensioni influenzano la mia visibilità?',
        answer:
          'Sì, le recensioni sono fondamentali. Incidono su: posizionamento in piattaforma, fiducia degli utenti e ranking interno.',
      },
      {
        question: 'Cosa succede se le performance non sono adeguate?',
        answer:
          "Klaro7 monitora costantemente la qualità del servizio. In caso di performance insufficienti o comportamenti non in linea con il codice etico, possono essere applicati: richiami, limitazioni, sospensione o chiusura dell'account.",
      },
    ],
  },
  {
    title: 'Etica e Comportamento',
    items: [
      {
        question: 'Ci sono regole etiche da rispettare?',
        answer:
          'Sì. Tutti i consulenti devono attenersi al codice etico Klaro7. Non è consentito: fare promesse certe sul futuro, creare dipendenza nel cliente, proporre rituali o pratiche non etiche. È richiesto: rispetto, professionalità, equilibrio comunicativo.',
      },
    ],
  },
  {
    title: 'Obiettivo Klaro7',
    items: [
      {
        question: 'Che tipo di consulenti cerca Klaro7?',
        answer:
          'Klaro7 seleziona consulenti che: lavorano con serietà e responsabilità, hanno capacità di ascolto e offrono un supporto reale e consapevole.',
      },
    ],
  },
];

function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <div className="border-b border-neutral-200 last:border-b-0">
      <button
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-4 py-3.5 px-1 text-left transition-colors hover:text-brand-teal"
      >
        <span className="text-sm md:text-base font-semibold text-neutral-900">{question}</span>
        <svg
          className={`h-5 w-5 shrink-0 text-brand-teal/60 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
      >
        <div className="overflow-hidden">
          <p className="px-1 pb-4 text-sm leading-relaxed text-neutral-600">{answer}</p>
        </div>
      </div>
    </div>
  );
}

function CandidacyForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'submitting' | 'done' | 'error'>('idle');
  const [error, setError] = useState('');

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const canSubmit = name.trim().length >= 2 && EMAIL_RE.test(email.trim()) && status !== 'submitting';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setStatus('submitting');
    setError('');
    try {
      await registerProfessionalLead({ name: name.trim(), email: email.trim() });
      setStatus('done');
    } catch (err) {
      setError(extractErrorMessage(err));
      setStatus('error');
    }
  };

  if (status === 'done') {
    return (
      <div className="mt-6 rounded-xl border border-white/20 bg-white/10 p-5 text-left">
        <p className="text-sm font-semibold text-white">Candidatura inviata! 🎉</p>
        <p className="mt-1 text-sm text-white/80">
          Ti abbiamo inviato un&apos;email a <strong>{email.trim()}</strong> con il link al questionario
          conoscitivo. Controlla anche lo spam. Completa il questionario per procedere con la selezione.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mx-auto mt-6 max-w-md text-left">
      <div className="space-y-3">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome e cognome"
          autoComplete="name"
          className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder-white/50 focus:border-brand-orange focus:outline-none"
        />
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="La tua email"
          autoComplete="email"
          className="w-full rounded-lg border border-white/20 bg-white/10 px-4 py-3 text-sm text-white placeholder-white/50 focus:border-brand-orange focus:outline-none"
        />
        {status === 'error' && <p className="text-sm text-red-300">{error}</p>}
        <button
          type="submit"
          disabled={!canSubmit}
          className="w-full rounded-full bg-brand-orange px-7 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-orange/90 disabled:opacity-50 min-h-[44px]"
        >
          {status === 'submitting' ? 'Invio in corso…' : 'Invia la tua candidatura'}
        </button>
        <p className="text-center text-xs text-white/50">
          Riceverai via email il link al questionario conoscitivo.
        </p>
      </div>
    </form>
  );
}

export function LavoraConNoiPage() {
  const [openKey, setOpenKey] = useState<string | null>(null);

  const handleToggle = (key: string) => {
    setOpenKey(openKey === key ? null : key);
  };

  const scrollToFaq = () => {
    document.getElementById('faq-consulenti')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="text-neutral-800">
      <section className="px-4 py-10">
        <div className="mx-auto max-w-3xl">
          {/* ── Hero reclutamento ── */}
          <div className="overflow-hidden rounded-2xl bg-gradient-to-r from-brand-navy via-[#1c1a4d] to-brand-teal-600 p-8 text-center shadow-sm md:p-12">
            <h1 className="text-2xl font-bold text-white md:text-4xl">
              Sei un professionista dell&apos;intuizione?
            </h1>
            <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-white/80 md:text-base">
              Stiamo costruendo la casa del futuro per la cartomanzia professionale. Cerchiamo
              esperti con etica, talento e voglia di crescere. Come Founder Partner, avrai
              commissioni agevolate e massima visibilità.
            </p>

            {/* Professional recruitment is always open — independent of the user
                waitlist flag (the candidacy hits /leads/register type=PROFESSIONAL,
                which the backend does not gate on waitlistEnabled). */}
            <CandidacyForm />

            <div className="mt-6 flex items-center justify-center gap-2">
              <button
                type="button"
                aria-label="FAQ Consulenti"
                title="Domande frequenti per i consulenti"
                onClick={scrollToFaq}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/40 text-lg font-semibold text-white/80 transition-colors hover:border-white/70 hover:text-white"
              >
                ?
              </button>
              <span className="text-xs text-white/60">Domande? Leggi le FAQ consulenti</span>
            </div>
          </div>

          {/* ── FAQ Consulenti ── */}
          <div id="faq-consulenti" className="mt-12 scroll-mt-24">
            <div className="mb-8 text-center">
              <h2 className="text-2xl font-bold text-neutral-900 md:text-3xl">FAQ Consulenti</h2>
              <p className="mt-2 text-sm text-neutral-500">
                Tutto quello che devi sapere per lavorare con Klaro7
              </p>
            </div>

            <div className="space-y-6">
              {categories.map((cat) => (
                <div key={cat.title}>
                  <h3 className="mb-3 text-base font-bold text-brand-teal md:text-lg">{cat.title}</h3>
                  <div className="rounded-2xl border border-neutral-200 bg-white px-5 py-2 shadow-sm md:px-6 md:py-3">
                    {cat.items.map((item, i) => {
                      const key = `${cat.title}-${i}`;
                      return (
                        <AccordionItem
                          key={key}
                          question={item.question}
                          answer={item.answer}
                          isOpen={openKey === key}
                          onToggle={() => handleToggle(key)}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-10 border-t border-neutral-200 pt-6">
            <Link to="/" className="text-sm font-medium text-brand-teal hover:underline">
              ← Torna alla home
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
